import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import useSchoolSettings from '../hooks/useSchoolSettings'
import { getApplicant } from './api/admissionApi'
import ApplicationDoc from './print/ApplicationDoc'
import SurrenderDoc from './print/SurrenderDoc'
import { nameParts } from './print/printFormat'
import { useLoading } from '../contexts/LoadingContext'
import './print/printDoc.css'
import '../print.css'

// หน้าพิมพ์เอกสารรับสมัคร — มีสองใบเหมือนระบบเดิม
//   1. ใบสมัครนักเรียนใหม่ (แบบฟอร์มต่างกันระหว่างแผนกมัธยม กับ อนุบาล-ประถม)
//   2. ใบมอบตัวนักเรียน (ใบเดียวใช้ทั้งสองแผนก)
// แต่ละใบมีปุ่มพิมพ์ของตัวเอง และ react-to-print จะพิมพ์เฉพาะใบนั้น

// ขนาดกระดาษต้องส่งผ่าน pageStyle ไม่ใช่ใส่ @page ในไฟล์ CSS
// เพราะ CRA รวม CSS ทั้งแอปเป็นสไตล์ชีตเดียว กฎ @page จึงหลุดไปทับหน้าอื่น
const PORTRAIT = '@page { size: A4 portrait; margin: 0.8cm; }'

export default function AdmissionPrint() {
  const { st_id } = useParams()
  const school = useSchoolSettings()
  const { setLoading } = useLoading()

  const [row, setRow] = useState(null)
  const [error, setError] = useState(null)

  const applicationRef = useRef(null)
  const surrenderRef = useRef(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    getApplicant(st_id)
      .then((data) => alive && setRow(data))
      .catch((err) => alive && setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [st_id, setLoading])

  // ตั้งชื่อไฟล์ให้เหมือนเดิม: "<เลขประจำตัว> <ชื่อเต็ม> ใบสมัคร"
  const docTitle = (suffix) => {
    if (!row) return suffix
    const idnumber = String(row.st_idnumber || '').trim()
    return `${idnumber} ${nameParts(row.st_name).full} ${suffix}`.trim()
  }

  const printApplication = useReactToPrint({
    contentRef: applicationRef,
    documentTitle: docTitle('ใบสมัคร'),
    pageStyle: PORTRAIT,
  })

  const printSurrender = useReactToPrint({
    contentRef: surrenderRef,
    documentTitle: docTitle('ใบมอบตัว'),
    pageStyle: PORTRAIT,
  })

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">{error}</Alert>
        <Button as={Link} to="/admission/applicants" variant="secondary">กลับหน้ารายชื่อ</Button>
      </div>
    )
  }
  if (!row) return <div className="p-4">กำลังโหลด…</div>

  const Toolbar = () => (
    <div className="text-center mb-2 d-print-none">
      <Button as={Link} to="/admission/applicants" variant="light" size="sm" className="mb-2 me-1">
        <i className="ri-arrow-left-line me-1" />กลับ
      </Button>
      <Button as={Link} to={`/admission/edit/${st_id}`} variant="light" size="sm" className="mb-2 me-1">
        <i className="ri-pencil-line me-1" />แก้ไข
      </Button>
      <Button variant="success" size="sm" className="mb-2 me-1" onClick={printApplication}>
        <i className="ri-printer-line me-1" />ปริ้นใบสมัคร
      </Button>
      <Button variant="primary" size="sm" className="mb-2 me-1" onClick={printSurrender}>
        <i className="ri-printer-line me-1" />ปริ้นใบมอบตัว
      </Button>
    </div>
  )

  return (
    <div className="p-3 p-lg-4" style={{ background: '#6c757d', minHeight: '100vh' }}>
      <Toolbar />
      <div className="adm-doc-scroll">
        <div className="adm-sheet">
          <div ref={applicationRef}>
            <ApplicationDoc row={row} school={school.school_name} />
          </div>
        </div>
      </div>

      <Toolbar />
      <div className="adm-doc-scroll">
        <div className="adm-sheet">
          <div ref={surrenderRef}>
            <SurrenderDoc
              row={row}
              school={school.school_name}
              schoolAddress={school.school_address}
              principal={school.principal_name}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
